

import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Avatar, 
  Tooltip, 
  IconButton,
  Paper,
  Popper,
  Fade,
  Badge,
  Chip,
  styled
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { stringToColor, getContrastText } from '../../utils/stringToColor';

// Styled components for enhanced design
const ContactAvatarWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
  position: 'relative',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

const ContactAvatar = styled(Avatar)(({ theme }) => ({
  width: 56,
  height: 56,
  fontSize: '1.25rem',
  fontWeight: 600,
  border: '3px solid white',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease'
}));

const ContactDetailPopover = styled(Paper)(({ theme }) => ({
  padding: 16,
  minWidth: 280,
  maxWidth: 320,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  borderRadius: 8,
  zIndex: 9999,
}));

const ContactDetail = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 12,
  padding: 8,
  borderRadius: 8,
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  '&:last-child': {
    marginBottom: 0
  }
}));

const CopyButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  marginLeft: 'auto',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.08)'
  }
}));

const ContactName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.85rem',
  textAlign: 'center',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginTop: 6,
}));

// Custom badge component for the primary contact
const PrimaryContactBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#FFD700',
    color: '#000',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    width: 20,
    height: 20,
    borderRadius: '50%',
    padding: 0,
    fontSize: '0.85rem',
    fontWeight: 'bold',
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'pulse 1.5s infinite ease-in-out',
      border: '1px solid #FFD700',
      content: '""',
    },
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '50%': {
      transform: 'scale(1.3)',
      opacity: 0.7,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
}));

const PrimaryChip = styled(Chip)(({ theme }) => ({
  height: 22,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: '#FFD700',
  color: '#000000',
  '& .MuiChip-label': {
    padding: '0 8px',
  }
}));

const ContactCard = ({ contactName, contactEmail, contactMobile, isPrimary = false, onClick, onCopyEmail, onCopyPhone }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(null);
  const open = Boolean(anchorEl);

  // Generate Avatar color based on name
  const avatarColor = stringToColor(contactName);
  const textColor = getContrastText(avatarColor);
  
  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handlePopper = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleCopy = (text, type, event) => {
    event.stopPropagation(); // keeps the card's onClick (navigation) from firing

    // Let the parent do the copying and show the snackbar
    if (type === 'email') {
      onCopyEmail && onCopyEmail();
    } else if (type === 'phone') {
      onCopyPhone && onCopyPhone();
    }

    // Only flip the tooltip to "Copied!" if there was actually something to copy
    if (text) {
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleClick = () => {
    if (onClick) onClick();
  };

  return (
    <ContactAvatarWrapper 
      onMouseEnter={handlePopper}
      onMouseLeave={() => setAnchorEl(null)}
      onClick={handleClick}
    >
      <PrimaryContactBadge
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        badgeContent={isPrimary ? '★' : 0}
        invisible={!isPrimary}
      >
        <ContactAvatar 
          sx={{ 
            bgcolor: avatarColor,
            color: textColor,
          }}
        >
          {getInitials(contactName)}
        </ContactAvatar>
      </PrimaryContactBadge>
      
      <ContactName>
        {contactName?.split(' ')[0] || 'Unknown'}
      </ContactName>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="top-start"
        transition
        style={{ zIndex: 9999 }}
        container={document.body}
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10], 
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: document.body,
              },
            },
            {
              name: 'flip',
              options: {
                altBoundary: true,
                fallbackPlacements: ['right', 'left', 'bottom'],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={250}>
            <ContactDetailPopover elevation={4}>
              <Box 
                sx={{ 
                  mb: 2, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {contactName || 'Unnamed Contact'}
                </Typography>
                
                {isPrimary && (
                  <PrimaryChip
                    label="Primary Contact"
                    size="small"
                  />
                )}
              </Box>

              <ContactDetail>
                <EmailIcon 
                  sx={{ 
                    color: '#5c6bc0', 
                    mr: 1.5, 
                    fontSize: '1.1rem' 
                  }} 
                />
                <Box sx={{ overflow: 'hidden', flex: 1 }}>
                  <Typography variant="body2" noWrap>
                    {contactEmail || 'No email provided'}
                  </Typography>
                </Box>
                <Tooltip 
                  title={copied === 'email' ? 'Copied!' : 'Copy email'} 
                  placement="top"
                >
                  <CopyButton 
                    size="small" 
                    onClick={(e) => handleCopy(contactEmail, 'email', e)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </CopyButton>
                </Tooltip>
              </ContactDetail>

              <ContactDetail>
                <PhoneIcon 
                  sx={{ 
                    color: '#43a047', 
                    mr: 1.5, 
                    fontSize: '1.1rem' 
                  }} 
                />
                <Box sx={{ overflow: 'hidden', flex: 1 }}>
                  <Typography variant="body2" noWrap>
                    {contactMobile || 'No phone provided'}
                  </Typography>
                </Box>
                <Tooltip 
                  title={copied === 'phone' ? 'Copied!' : 'Copy phone'} 
                  placement="top"
                >
                  <CopyButton 
                    size="small" 
                    onClick={(e) => handleCopy(contactMobile, 'phone', e)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </CopyButton>
                </Tooltip>
              </ContactDetail>
            </ContactDetailPopover>
          </Fade>
        )}
      </Popper>
    </ContactAvatarWrapper>
  );
};

export default ContactCard;
